import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle, makeStyles } from '@fluentui/react-components';
import { motion } from 'framer-motion';

import { ServicePrincipalView } from '../../api/tenantRegistry';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { SectionLabel } from '../../components/detailPrimitives';
import { useTenantDetailQuery } from '../../hooks/useTenantDetail';

const useStyles = makeStyles({
  tableSurface: {
    backgroundColor: '#ffffff',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: '#e4e4e7',
    borderRightColor: '#e4e4e7',
    borderBottomColor: '#e4e4e7',
    borderLeftColor: '#e4e4e7',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowX: 'auto',
  },
});

// Service Principals tab (FD-158, ADR-023 §5). The ADR-020 tenant-app enterprise
// application as it exists in this tenant. Full identifiers (objectId, appId,
// role names) are surfaced to authenticated admins — configuration identifiers,
// not secrets. Monospace for all IDs.
export default function TenantServicePrincipalsPage() {
  const styles = useStyles();
  const { tenantId } = useParams<{ tenantId: string }>();
  const query = useTenantDetailQuery(tenantId);
  const tenant = query.data;

  const columns = useMemo<DataTableColumn<ServicePrincipalView>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Display name',
        render: (sp) => (
          <span className="font-display font-medium text-zinc-900">
            {sp.displayName || <span className="text-zinc-400">—</span>}
          </span>
        ),
      },
      {
        key: 'appId',
        header: 'App (client) ID',
        render: (sp) => (
          <span className="font-mono text-xs text-zinc-800 break-all" title={sp.appId}>
            {sp.appId}
          </span>
        ),
      },
      {
        key: 'objectId',
        header: 'Object ID',
        render: (sp) => (
          <span className="font-mono text-xs text-zinc-800 break-all" title={sp.objectId}>
            {sp.objectId}
          </span>
        ),
      },
      {
        key: 'appRoles',
        header: 'Granted app roles',
        render: (sp) =>
          sp.appRoles.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {sp.appRoles.map((role) => (
                <span
                  key={role}
                  className="font-mono text-xs text-zinc-700 bg-zinc-100 border border-zinc-200 rounded px-1.5 py-0.5"
                >
                  {role}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-zinc-400">—</span>
          ),
      },
    ],
    [],
  );

  if (!tenant) return null;

  const emptyNode = (
    <MessageBar intent="info">
      <MessageBarBody>
        <MessageBarTitle>No service principals</MessageBarTitle>
        The tenant-app enterprise application has not been consented into this tenant yet, or
        the last refresh could not enumerate it. Trigger admin consent from the Overview tab,
        then Re-check.
      </MessageBarBody>
    </MessageBar>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-5"
    >
      <div>
        <SectionLabel>Service principals</SectionLabel>
        <p className="font-display text-sm text-zinc-600 leading-relaxed max-w-[65ch] -mt-2">
          The AirBridge application as it appears in this tenant&rsquo;s Enterprise applications,
          and the Microsoft Graph permissions granted to it. Full identifiers are shown for
          troubleshooting; no secrets or certificates are ever shown.
        </p>
      </div>

      <div className={styles.tableSurface}>
        <DataTable
          columns={columns}
          data={tenant.servicePrincipals}
          keyForRow={(sp) => sp.objectId}
          emptyState={emptyNode}
        />
      </div>
    </motion.div>
  );
}
